import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { KasiaGoalTrack } from "@/lib/api";
import { formatAxisMoney } from "@/lib/chartSeriesAxes";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn, formatPLN } from "@/lib/utils";

function paceLabel(pace: string): string {
  if (pace === "powyzej") return "powyżej celu";
  if (pace === "ponizej") return "poniżej celu";
  return "blisko celu";
}

function paceColor(pace: string): string {
  if (pace === "powyzej") return "text-emerald-600";
  if (pace === "ponizej") return "text-rose-600";
  return "text-amber-600";
}

export default function GoalTrackChart({
  data,
  className,
}: {
  data: KasiaGoalTrack;
  className?: string;
}) {
  const month = data.month;
  const lookback = month.lookback_weeks || 8;
  const chartData = (data.weeks ?? []).map((w) => ({
    label: w.label_pl,
    revenue: w.revenue,
    is_partial: w.is_partial,
    iso_week: w.iso_week,
  }));

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          Cel {formatPLN(data.goal_monthly_pln)} / miesiąc
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {month.label_pl} · performance tygodniami · linia = cel tygodnia (
          {formatPLN(data.goal_weekly_pln)})
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <p className="text-xs text-muted-foreground">Od początku miesiąca</p>
            <p className="text-lg font-semibold tabular-nums">
              {formatPLN(month.revenue_mtd)}
            </p>
            <p className="text-xs text-muted-foreground">
              dzień {month.days_elapsed}/{month.days_in_month}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">% celu</p>
            <p className="text-lg font-semibold tabular-nums">
              {month.pct_of_goal.toLocaleString("pl-PL", {
                maximumFractionDigits: 1,
              })}
              %
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">
              Prognoza końca miesiąca
            </p>
            <p className={cn("text-lg font-semibold tabular-nums", paceColor(month.pace_vs_goal))}>
              {formatPLN(month.projected_pln)}
            </p>
            <p className="text-xs text-muted-foreground">
              średnia z {lookback} tyg. · {paceLabel(month.pace_vs_goal)}
            </p>
          </div>
        </div>

        {chartData.length === 0 ? (
          <p className="text-sm text-muted-foreground">Brak danych tygodniowych.</p>
        ) : (
          <div className="h-[240px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="label"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                  tickFormatter={formatAxisMoney}
                  width={44}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(value: number) => [formatPLN(Number(value) || 0), "Przychód"]}
                  labelFormatter={(label, payload) => {
                    const row = payload?.[0]?.payload as
                      | { iso_week?: string; is_partial?: boolean }
                      | undefined;
                    const week = row?.iso_week || String(label);
                    return row?.is_partial ? `${week} (bieżący)` : week;
                  }}
                />
                <ReferenceLine
                  y={data.goal_weekly_pln}
                  stroke="hsl(var(--muted-foreground))"
                  strokeDasharray="4 4"
                  label={{
                    value: "cel tyg.",
                    position: "insideTopRight",
                    fill: "hsl(var(--muted-foreground))",
                    fontSize: 10,
                  }}
                />
                <Bar dataKey="revenue" name="Przychód" radius={[3, 3, 0, 0]} maxBarSize={28}>
                  {chartData.map((entry) => (
                    <Cell
                      key={entry.iso_week}
                      fill={
                        entry.is_partial
                          ? "hsl(var(--brand) / 0.45)"
                          : "hsl(var(--brand))"
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
